import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { AlertCircle, ArrowRight, Info, Loader2 } from "lucide-react";
import { fetchConfig, fetchJob, submitApplication, uploadApplicationFile, type Criteria, type SubmitResult } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button, buttonClasses } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Container } from "@/components/container";
import { Turnstile } from "@/components/turnstile";
import { EagerFileInput, useEagerUpload } from "@/components/eager-file-input";
import { applicationSubmissionFailure, WRITE_MAINTENANCE_MESSAGE } from "../../shared/submission-messages";

export const Route = createFileRoute("/jobs/$slug/apply")({ component: ApplyPage });

const DEFAULT_QUICK_CHECK: Required<Criteria> = {
  title: "Quick check",
  description: "Continue only if every required criterion matches your experience. These are hard filters, not preferences.",
  bullets: [
    "Confirm the required criteria before sending your application.",
    "Attach a current resume.",
    "Share optional links that best represent your work.",
  ],
  info: "False confirmations permanently disqualify future applications for this role.",
};

function resolveCriteria(config: Criteria | null | undefined, fallback: Required<Criteria>): Required<Criteria> {
  return {
    title: config?.title ?? fallback.title,
    description: config?.description ?? fallback.description,
    bullets: config?.bullets?.length ? config.bullets : fallback.bullets,
    info: config?.info ?? fallback.info,
  };
}


function formatSubmitError(result: SubmitResult): string {
  switch (result.error) {
    case "write_maintenance":
      return WRITE_MAINTENANCE_MESSAGE;
    case "turnstile_failed":
      return "Verification failed. Refresh the page, complete verification again, and resubmit.";
    case "validation":
      return `Some fields didn't validate: ${JSON.stringify(result.issues ?? {})}`;
    case "prereqs_not_met":
      return "One or more required criteria were not confirmed.";
    case "file_too_large":
      return "Your uploaded file is larger than 10MB. Please attach a smaller file.";
    case "file_type_blocked":
      return "That file type isn't allowed. Upload a PDF, DOC, or similar document.";
    case "upload_missing":
      return "Your uploaded file was missing. Please choose the file again.";
    case "already_applied":
      return result.linear?.identifier
        ? `You've already applied for this role (${result.linear.identifier}).`
        : "You've already applied for this role.";
    case "applications_full":
      return "Applications for this role are currently full.";
    case "applications_disabled":
      return "Applications for this role are currently disabled.";
    case "not_found":
      return "This job is no longer accepting applications.";
    case "job_updated":
      return "This role changed while the form was open. Refresh the page, review the updated criteria, and submit again.";
    case "linear_failed":
    case "internal_error":
    default:
      return applicationSubmissionFailure(result.submission_id);
  }
}

function ApplyPage() {
  const { slug } = useParams({ from: "/jobs/$slug/apply" });
  const navigate = useNavigate();
  const jobQuery = useQuery({ queryKey: ["job", slug], queryFn: () => fetchJob(slug) });
  const configQuery = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resumeUpload = useEagerUpload((file, onProgress) => uploadApplicationFile(slug, file, onProgress));

  const requiredIds = useMemo(() => (jobQuery.data?.prerequisites ?? []).filter((p) => p.required === 1).map((p) => p.id), [jobQuery.data]);
  const acceptedRequiredCount = requiredIds.filter((id) => checked[id]).length;
  const allRequiredChecked = requiredIds.every((id) => checked[id]);
  const quickCheck = resolveCriteria(jobQuery.data?.criteria, DEFAULT_QUICK_CHECK);
  const uploadsBusy = resumeUpload.status === "uploading";
  const uploadsFailed = resumeUpload.status === "error";

  if (jobQuery.isLoading || configQuery.isLoading) {
    return (
      <Container width="lg" className="py-16">
        <div className="space-y-6">
          <Skeleton className="h-20 w-3/4" />
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </Container>
    );
  }

  if (jobQuery.isError || !jobQuery.data || configQuery.isError || !configQuery.data) {
    return (
      <Container width="lg" className="py-16">
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Could not load the application form.</AlertTitle>
          <AlertDescription>Try again in a moment, or return to all open positions.</AlertDescription>
        </Alert>
        <Link to="/" className={buttonClasses({ variant: "outline", className: "mt-6" })}>
          All positions
        </Link>
      </Container>
    );
  }

  const { job } = jobQuery.data;
  const { turnstile_site_key } = configQuery.data;

  if (job.status === "disabled") {
    return (
      <Container width="lg" className="py-12">
        <Link to="/jobs/$slug" params={{ slug }} className="text-sm text-muted-foreground hover:text-foreground">
          ← {job.title}
        </Link>
        <Alert className="mt-6">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Applications disabled</AlertTitle>
          <AlertDescription>We are not accepting new applications for this role right now.</AlertDescription>
        </Alert>
        <Link to="/" className={buttonClasses({ variant: "outline", className: "mt-6" })}>All positions</Link>
      </Container>
    );
  }

  if (job.applications_full) {
    return (
      <Container width="lg" className="py-12">
        <Link to="/jobs/$slug" params={{ slug }} className="text-sm text-muted-foreground hover:text-foreground">
          ← {job.title}
        </Link>
        <Alert className="mt-6">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Applications closed</AlertTitle>
          <AlertDescription>We've hit the cap for this role and are no longer accepting new applications. We’ll be back soon after reviewing the current open applications.</AlertDescription>
        </Alert>
        <Link to="/" className={buttonClasses({ variant: "outline", className: "mt-6" })}>All positions</Link>
      </Container>
    );
  }

  async function onSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setError(null);
    if (!allRequiredChecked) {
      setError("Confirm every required criterion before submitting.");
      return;
    }
    if (!token) {
      setError("Please complete the verification check.");
      return;
    }
    if (resumeUpload.status !== "ready") {
      setError(resumeUpload.status === "uploading" ? "Wait for the resume upload to finish." : "Choose your resume again.");
      return;
    }
    setSubmitting(true);
    try {
      const accepted = Object.entries(checked).filter(([, value]) => value).map(([key]) => key);
      const fd = new FormData(ev.currentTarget);
      fd.set("turnstile_token", token);
      fd.set("prereqs_accepted", JSON.stringify(accepted));
      fd.set("file_upload_id", resumeUpload.uploadId);
      if (job.jd_version) fd.set("jd_version", job.jd_version);
      const result = await submitApplication(slug, fd);
      if (result.ok) {
        void navigate({ to: "/jobs/$slug/submitted", params: { slug }, search: { sid: result.submission_id } });
        return;
      }
      setError(formatSubmitError(result));
    } catch {
      setError(applicationSubmissionFailure());
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container width="lg" className="py-12">
      <Link to="/jobs/$slug" params={{ slug }} className="text-sm text-muted-foreground hover:text-foreground">
        ← {job.title}
      </Link>

      <div className="mt-8 grid gap-12 lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start">
        <aside className="lg:sticky lg:top-24">
          <Badge variant="outline" className="mb-4 bg-background font-normal">Apply</Badge>
          <h1 className="text-2xl font-semibold tracking-tight">{quickCheck.title}</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">{quickCheck.description}</p>
          <ol className="mt-8 space-y-5 text-sm">
            {quickCheck.bullets.map((description) => (
              <li key={description} className="grid grid-cols-[0.4rem_1fr] gap-2">
                <span className="mt-[0.72rem] h-1.5 w-1.5 rounded-full bg-muted-foreground/55" aria-hidden="true" />
                <span className="block leading-6 text-muted-foreground">{description}</span>
              </li>
            ))}
          </ol>
          {quickCheck.info && (
            <p className="mt-8 flex gap-2 text-sm leading-6 text-muted-foreground">
              <Info className="mt-1 size-4 shrink-0" aria-hidden="true" />
              <span>{quickCheck.info}</span>
            </p>
          )}
        </aside>

        <main className="min-w-0">
          <form onSubmit={onSubmit} className="space-y-8">
            <section>
              <p className="mb-5 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Required criteria</p>
              <div className="border-y">
                {jobQuery.data.prerequisites.map((prerequisite) => (
                  <Checkbox
                    key={prerequisite.id}
                    id={`pre_${prerequisite.id}`}
                    label={prerequisite.label}
                    checked={!!checked[prerequisite.id]}
                    onCheckedChange={(value) => setChecked((state) => ({ ...state, [prerequisite.id]: value }))}
                  />
                ))}
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                {allRequiredChecked ? "All required criteria confirmed." : `${acceptedRequiredCount}/${requiredIds.length} required confirmed.`}
              </p>
            </section>

            {allRequiredChecked ? (
              <>
                <section>
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Application details</p>
                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your name</Label>
                      <Input id="name" name="name" required maxLength={200} autoComplete="name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" name="email" type="email" required maxLength={200} autoComplete="email" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone <span className="font-normal text-muted-foreground">(optional)</span></Label>
                      <Input id="phone" name="phone" type="tel" maxLength={50} autoComplete="tel" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="linkedin_url">LinkedIn URL <span className="font-normal text-muted-foreground">(optional)</span></Label>
                      <Input id="linkedin_url" name="linkedin_url" type="url" placeholder="https://www.linkedin.com/in/you" maxLength={500} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="demo_url">Demo URL <span className="font-normal text-muted-foreground">(optional)</span></Label>
                      <Input id="demo_url" name="demo_url" type="url" placeholder="https://demo.example.com" maxLength={500} />
                      <p className="flex gap-2 text-xs leading-5 text-muted-foreground">
                        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                        <span>Strong plus for frontend and design roles.</span>
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="github_url">GitHub URL <span className="font-normal text-muted-foreground">(optional)</span></Label>
                      <Input id="github_url" name="github_url" type="url" placeholder="https://github.com/you" maxLength={500} />
                      <p className="flex gap-2 text-xs leading-5 text-muted-foreground">
                        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                        <span>Good hobby projects on GitHub are a strong plus.</span>
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <EagerFileInput
                    id="file"
                    label={<>Resume <span className="font-normal text-muted-foreground">(required, up to 10MB)</span></>}
                    upload={resumeUpload}
                    required
                    disabled={submitting}
                  />
                </section>

                <Turnstile siteKey={turnstile_site_key} onToken={setToken} />

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle aria-hidden="true" />
                    <AlertTitle>Submission failed</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button type="submit" size="lg" disabled={submitting || uploadsBusy || uploadsFailed}>
                    {submitting || uploadsBusy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
                    {submitting ? "Submitting…" : uploadsBusy ? "Uploading…" : "Submit application"}
                  </Button>
                  <Link to="/jobs/$slug" params={{ slug }} className={buttonClasses({ variant: "outline", size: "lg" })}>
                    Cancel
                  </Link>
                </div>
              </>
            ) : (
              <Alert>
                <Info aria-hidden="true" />
                <AlertTitle>Application form locked</AlertTitle>
                <AlertDescription>Confirm every required criterion above to continue to the application form.</AlertDescription>
              </Alert>
            )}
          </form>
        </main>
      </div>
    </Container>
  );
}
